import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExcelSheetData, ImportRow, ColumnMappingEntry, BudgetImportField, BUDGET_IMPORT_FIELDS } from '../utils/types';
import styles from './StepMapping.module.scss';

interface Props {
    excelData: ExcelSheetData;
    headerRowNumber: number;
    columnMappings: ColumnMappingEntry[];
    importRows: ImportRow[];
    onColumnMappingChange: (colIndex: number, field: BudgetImportField | null) => void;
    onRowTypeChange: (rowIndex: number, type: 'S' | 'O' | null) => void;
    onHeaderRowChange: (row: number) => void;
    onCellValueChange: (rowIndex: number, colIndex: number, value: string) => void;
}

const StepMapping: React.FC<Props> = ({
    excelData, headerRowNumber, columnMappings, importRows, onColumnMappingChange, onRowTypeChange, onHeaderRowChange, onCellValueChange,
}) => {
    const { t } = useTranslation();

    const allRows = excelData.rows;
    const headerRowIdx = headerRowNumber - 1;
    const headerRow = allRows[headerRowIdx] ?? [];

    // All fields can be mapped to multiple columns; values are concatenated with space
    const getAvailableFields = () => BUDGET_IMPORT_FIELDS;

    const mappedFields = new Set(columnMappings.filter(m => m.budgetField).map(m => m.budgetField));
    const allFieldsMapped = BUDGET_IMPORT_FIELDS.every(f => mappedFields.has(f.value));

    const numberColIndices = useMemo(() => new Set(
        columnMappings.filter(m => m.budgetField === 'number').map(m => m.excelColumnIndex)
    ), [columnMappings]);

    // Find columns that have data in at least one row (across all rows)
    const visibleColIndices = useMemo(() => {
        const maxCols = Math.max(...allRows.map(r => r.length), 0);
        return Array.from({ length: maxCols }, (_, idx) => idx)
            .filter(colIdx => allRows.some(row => row[colIdx]?.trim()));
    }, [allRows]);

    // Build a map from data row index to ImportRow for quick lookup
    const importRowMap = useMemo(() => {
        const map = new Map<number, ImportRow>();
        importRows.forEach(r => map.set(r.rowIndex, r));
        return map;
    }, [importRows]);

    return (
        <div className={styles.container}>
            <div className={styles.description}>
                <p>{t('budgets.budget.import_structure.step_mapping_description_1')}</p>
                <p>{t('budgets.budget.import_structure.step_mapping_description_2')}</p>
                <p>{t('budgets.budget.import_structure.step_mapping_description_3')}</p>
                <p>{t('budgets.budget.import_structure.step_mapping_description_4')}</p>
            </div>

            {!allFieldsMapped && (
                <div className={styles.warning}>
                    {t('budgets.budget.import_structure.error_all_fields_required')}
                </div>
            )}

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        {/* Mapping row - sticky at top */}
                        <tr className={styles.mappingRow}>
                            <th className={styles.radioCol}></th>
                            <th className={styles.typeCol}>
                                {t('budgets.budget.import_structure.field_type')}
                            </th>
                            {visibleColIndices.map(colIndex => {
                                const mapping = columnMappings.find(m => m.excelColumnIndex === colIndex);
                                const currentField = mapping?.budgetField ?? null;
                                const availableFields = getAvailableFields();

                                return (
                                    <th key={colIndex} className={styles.mappingCell}>
                                        <select
                                            value={currentField ?? ''}
                                            onChange={(e) => onColumnMappingChange(
                                                colIndex,
                                                e.target.value ? e.target.value as BudgetImportField : null
                                            )}
                                            className={styles.mappingSelect}
                                        >
                                            <option value="">{t('budgets.budget.import_structure.field_not_mapped')}</option>
                                            {availableFields.map(f => (
                                                <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
                                            ))}
                                        </select>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {allRows.map((row, rowIdx) => {
                            const isHeader = rowIdx === headerRowIdx;
                            const isAboveHeader = rowIdx < headerRowIdx;
                            const dataRowIndex = rowIdx - headerRowNumber;
                            const importRow = !isHeader && !isAboveHeader ? importRowMap.get(dataRowIndex) : null;

                            const rowClassName = [
                                styles.dataRow,
                                isHeader ? styles.headerSelectedRow : '',
                                isAboveHeader ? styles.aboveHeaderRow : '',
                                !isHeader && !isAboveHeader && importRow?.type === null ? styles.warningRow : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <tr key={rowIdx} className={rowClassName}>
                                    <td className={styles.radioCol}>
                                        <input
                                            type="radio"
                                            name="headerRow"
                                            checked={isHeader}
                                            onChange={() => onHeaderRowChange(rowIdx + 1)}
                                            className={styles.radioInput}
                                        />
                                    </td>
                                    <td className={styles.typeCol}>
                                        {!isHeader && !isAboveHeader && importRow && (
                                            <select
                                                value={importRow.type ?? ''}
                                                onChange={(e) => onRowTypeChange(
                                                    importRow.rowIndex,
                                                    e.target.value
                                                        ? e.target.value as 'S' | 'O'
                                                        : null
                                                )}
                                                className={`${styles.typeSelect} ${importRow.type === 'S' ? styles.typeSection : ''} ${importRow.type === null ? styles.typeUnknown : ''}`}
                                            >
                                                <option value="">{t('budgets.budget.import_structure.type_skip')}</option>
                                                <option value="S">{t('budgets.budget.import_structure.type_section')}</option>
                                                <option value="O">{t('budgets.budget.import_structure.type_note')}</option>
                                            </select>
                                        )}
                                    </td>
                                    {visibleColIndices.map(colIndex => {
                                        const isNumberCol = numberColIndices.has(colIndex);
                                        const isEditable = isNumberCol && !isHeader && !isAboveHeader && importRow;
                                        const cellValue = isEditable
                                            ? (importRow.rawCells[colIndex] ?? '')
                                            : row[colIndex];

                                        return (
                                            <td key={colIndex} className={isHeader
                                                ? styles.headerCell
                                                : styles.dataCell} title={row[colIndex]}>
                                                {isEditable
                                                    ? (
                                                        <input
                                                            type="text"
                                                            value={cellValue}
                                                            onChange={(e) => onCellValueChange(importRow.rowIndex, colIndex, e.target.value)}
                                                            className={styles.editableInput}
                                                        />
                                                    )
                                                    : (
                                                        row[colIndex]
                                                    )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StepMapping;
